
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, IconButton, Tooltip, Menu, MenuItem, Popper, Paper, ListItemText, ListItemIcon } from "@mui/material";
import {
	MRT_ShowHideColumnsButton,
	MRT_ToggleFiltersButton,
	MRT_ToggleGlobalFilterButton,
	MaterialReactTable,
	useMaterialReactTable,
} from "material-react-table";

import DeleteIcon from "@mui/icons-material/Delete";

import "./MyFleetTable.scss";

import { Button, COLORS, Modal } from "abs-ui-components";

import AbsModal from "../../common/absModal/AbsModal";
import axiosHttp from "../../services/interceptor";
import ImportVessel from "../ImportVessel/ImportVessel";
import { emitToasterMsg } from "../../services/toasterUtility";
import { convertLocaltoUTCFormat } from "../../services/DateFormatUtility";
import { ClearIcon } from "@mui/x-date-pickers";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const MyFleetTable = (props) => {
	const navigate = useNavigate();
	const [confirmDelete, setConfirmDelete] = useState(false);
	const [myFleetData, setMyFleetData] = useState([]);
	const [myFleetColumnFilters, setMyFleetColumnFilters] = useState([]);
	const [rowCount, setRowCount] = useState(0);
	const [myFleetGlobalFilter, setMyFleetGlobalFilter] = useState("");
	const [myFleetSort, setMyFleetSort] = useState([{ id: "ModifiedDate", desc: true }]);
	const [loading, setLoading] = useState(true);
	const [myFleetPagination, setMyFleetPagination] = useState({
		pageIndex: 0,
		pageSize: 25,
	});
	const [selectAll, setSelectAll] = useState(false);
	const [isImportVesselModalOpen, setIsImportVesselModalOpen] = useState(false);
	const [selectedChildReport, setSelectedChildReport] = useState(null);
	const [previousGlobalFilter, setPreviousGlobalFilter] = useState("");
	const [searchInput, setSearchInput] = useState('');
	const [columnFilterInputs, setColumnFilterInputs] = useState({});
	const [pendingColumnFilters, setPendingColumnFilters] = useState([]); // ✅ ONLY for API

	const toggleImportVessel = (success) => {
		setIsImportVesselModalOpen(!isImportVesselModalOpen);
		if (success) {
			props.refetchVesselList();
		}
	};

	useEffect(() => {
		let sortOptions = myFleetSort.map((x) => ({
			sortField: x.id,
			isAscending: !x.desc,
		}));
		// ✅ COLUMN FILTERS (ENTER triggered only) 
		if (myFleetColumnFilters.length > 0) {
			searchVessels( myFleetGlobalFilter, myFleetPagination.pageIndex, myFleetPagination.pageSize, sortOptions);
		} else {
			if (myFleetGlobalFilter !== "" && previousGlobalFilter !== myFleetGlobalFilter) {
				setPreviousGlobalFilter(myFleetGlobalFilter);
				setMyFleetPagination({ ...myFleetPagination, pageIndex: 0 });
				searchVessels(myFleetGlobalFilter, 0, myFleetPagination.pageSize, sortOptions);
			} else if (sortOptions.length === 0) {
				searchVessels("", myFleetPagination.pageIndex, myFleetPagination.pageSize, [
					{
						sortField: "ModifiedDate",
						isAscending: false,
					},
				]);
			} else {
				searchVessels(myFleetGlobalFilter, myFleetPagination.pageIndex, myFleetPagination.pageSize, sortOptions);
			}
		}
	}, [myFleetGlobalFilter, myFleetColumnFilters, myFleetSort, props.onRefetchVesselList, myFleetPagination.pageIndex, myFleetPagination.pageSize]);

	const handleDeleteBtnClick = (row) => {
		setConfirmDelete(true);
		setSelectedChildReport(row.original);
	};

	const handleDeletingRecord = () => {
		axiosHttp({
			url: `api/MyFleet/DeleteVessels`,
			method: "POST",
			data: { vesselMstIds: [selectedChildReport?.VesselMstId] },
		})
			.then((res) => {
				setConfirmDelete(false);
				if (res && res?.data?.isSuccess) {
					emitToasterMsg("success", "Vessel(s)", "Deleted Successfully");
					props.refetchVesselList();
				} else if (!res?.data?.isSuccess) {
					emitToasterMsg("error", res?.data?.errorCode);
				}
			})
			.catch((error) => {
				setConfirmDelete(false);
				console.error("error", "Vessel(s)", error?.response?.data?.errorMessage);
			});
	};

	const searchVessels = (searchStr = "", pageIndex = 0, pageSize = 25, sortOption) => {
		let filtersArray = myFleetColumnFilters.map((filter) => ({
			key: filter.id,
			value: filter.value,
		}));

		let requestPayload = {
			globalFilter: searchStr,
			filters: filtersArray,
			pagination: {
				page: pageIndex + 1,
				pageSize: pageSize,
			},
			sortOption: { ...sortOption[0] },
		};
		setLoading(true);

		axiosHttp
			.post(`${BASE_URL}api/MyFleet/GetMyFleet`, requestPayload)
			.then((response) => {
				if (response?.data?.Data?.Data) {
					setMyFleetData(response?.data?.Data?.Data);
					setRowCount(response?.data?.Data?.Pagination?.TotalItems);
				}
				setLoading(false);
			})
			.catch((error) => {
				setMyFleetData([]);
				setLoading(false);
			});
	};

	const filterVessel = (columnFilterValue, pageIndex = 0, pageSize = 25, sortOption) => {
		let filtersArray = columnFilterValue.map((filter) => ({
			key: filter.id,
			value: filter.value,
		}));
		let requestPayload = {
			globalFilter: "",
			filters: filtersArray,
			pagination: {
				page: pageIndex + 1,
				pageSize: pageSize,
			},
			sortOption: { ...sortOption[0] },
		};
		setLoading(true);

		axiosHttp
			.post(`${BASE_URL}api/MyFleet/GetMyFleet`, requestPayload)
			.then((response) => {
				if (response?.data?.Data?.Data) {
					setMyFleetData(response?.data?.Data?.Data);
					setRowCount(response?.data?.Data?.Pagination?.TotalItems);
				}
				setLoading(false);
			})
			.catch((error) => {
				setMyFleetData([]);
				setLoading(false);
			});
	};

	const myFleetTableColumns = useMemo(
		() => [
			{
				enableSorting: false,
				enableColumnActions: false,
				enableColumnFilters: false,
				enableHiding: false,
				accessorKey: "delete",
				enableEditing: false,
				Filter: () => null,
				size: 100,
				Cell: ({ row }) => {
					return (
						<Box sx={{ display: "flex", gap: "1rem" }}>
							<Tooltip title="Delete">
								<IconButton
									color="error"
									onClick={(e) => {
										e.stopPropagation();
										e.preventDefault();
										handleDeleteBtnClick(row, true);
									}}
								>
									<DeleteIcon />
								</IconButton>
							</Tooltip>
						</Box>
					);
				},
			},
			{
				header: "WCN",
				enableHiding: false,
				accessorKey: "WcnNumber",
			},
			{
				header: "Company Name",
				accessorKey: "CompanyName",
			},
			{
				header: "IMO Number",
				enableHiding: false,
				accessorKey: "ImoNumber",
			},
			{
				header: "Vessel Name",
				accessorKey: "VesselName",
			},
			{
				header: "IMO Vessel Type",
				accessorKey: "ImoVesselTypeName",
			},
			{
				header: "EU Vessel Type",
				accessorKey: "MrvVesselTypeName",
			},
			{
				header: "UK Vessel Type",
				accessorKey: "UkVesselTypeName",
			},
			{
				header: "Flag",
				accessorKey: "Flag",
			},
			{
				header: "Last Modified",
				accessorKey: "LastModified",
				Cell: ({ row: { original = {} } }) => <span>{convertLocaltoUTCFormat(original.LastModified)}</span>,
			},
		],
		[handleDeletingRecord]
	);

	const handleMyFleetTableRowClick = (e, row) => {
		const vesselId = row?.original?.VesselMstId;
		const url = `/my-fleet/enroll-vessel/${vesselId}`;
		if (e?.ctrlKey || e?.metaKey || e?.button === 1) {
			window.open(url, "_blank");
		} else {
			navigate(url);
		}
	};
	const handleSelectAll = (event) => {
		setSelectAll(event.target.checked);
	};

	const applyFilters = useCallback(() => {

		setMyFleetPagination((prev) => ({
			...prev,
			pageIndex: 0,
		}));

		setMyFleetColumnFilters([
			...pendingColumnFilters,
		]);

		setMyFleetGlobalFilter(searchInput);

	}, [pendingColumnFilters, searchInput]);

	const resetFilterForColumn = useCallback((columnId) => {

		setMyFleetPagination((prev) => ({
			...prev,
			pageIndex: 0,
		}));

		setMyFleetColumnFilters((prev) =>
			prev.filter((f) => f.id !== columnId)
		);

	}, []);

	const handleColumnFiltersChange = useCallback(
		(updater) => {

			setPendingColumnFilters((prev) => {

				const updated =
					typeof updater === "function"
						? updater(prev)
						: updater;

				// detect removed filters
				prev.forEach((oldFilter) => {

					const stillExists = updated.some(
						(newFilter) =>
							newFilter.id === oldFilter.id
					);

					if (!stillExists) {

						// ONLY if previously applied
						const wasApplied =
							myFleetColumnFilters.some(
								(f) => f.id === oldFilter.id
							);

						if (wasApplied) {
							resetFilterForColumn(oldFilter.id);
						}
					}
				});

				return updated;
			});
		},
		[myFleetColumnFilters, resetFilterForColumn]
	);

	const updateFilterValue = useCallback(
		(columnId, value) => {

			setPendingColumnFilters((prev) => {

				const normalizedValue =
					value?.trim?.() ?? "";

				const others = prev.filter(
					(f) => f.id !== columnId
				);

				const updatedPending = normalizedValue
					? [
						...others,
						{
							id: columnId,
							value: normalizedValue,
						},
					]
					: others;

				// ONLY auto reset if filter was already applied
				const wasApplied =
					myFleetColumnFilters.some(
						(f) => f.id === columnId
					);

				if (!normalizedValue && wasApplied) {

					setMyFleetPagination((prev) => ({
						...prev,
						pageIndex: 0,
					}));

					setMyFleetColumnFilters((prevApplied) =>
						prevApplied.filter(
							(f) => f.id !== columnId
						)
					);
				}

				return updatedPending;
			});
		},
		[myFleetColumnFilters]
	);

	const clearAllFilters = useCallback(() => {

		setPendingColumnFilters([]);

		setMyFleetColumnFilters([]);

		setSearchInput("");

		setMyFleetGlobalFilter("");

		setMyFleetPagination((prev) => ({
			...prev,
			pageIndex: 0,
		}));

	}, []);

	const myFleetTable = useMaterialReactTable({
		columns: myFleetTableColumns,
		data: myFleetData,
		enableStickyHeader: true,
		enableTopToolbar: true,
		manualPagination: true,
		manualFiltering: true,
		manualSorting: true,
		rowCount,
		getRowId: (row) => row.VesselMstId,
		onPaginationChange: setMyFleetPagination,
		onSortingChange: setMyFleetSort,
		onColumnFiltersChange: handleColumnFiltersChange, // 👈 override default to disable auto filter changes
		state: {
			pagination: myFleetPagination,
			globalFilter: searchInput,
			columnFilters: pendingColumnFilters,
			sorting: myFleetSort,
			isLoading: loading,
		},
		initialState: {
			density: "compact",
			columnOrder: [
				"delete",
				"action",
				"WcnNumber",
				"CompanyName",
				"ImoNumber",
				"VesselName",
				"ImoVesselTypeName",
				"MrvVesselTypeName",
				"UkVesselTypeName",
				"Flag",
				"LastModified",
			],
		},
		muiTableBodyRowProps: ({ row }) => ({
			component: "a",
			href: `/my-fleet/enroll-vessel/${row?.original?.VesselMstId}`,
			onClick: (e) => {
				if (!e.defaultPrevented) {
					handleMyFleetTableRowClick(e, row);
				}
			},
			sx: {
				cursor: "pointer",
				textDecoration: "none",
				color: "inherit",
			},
		}),
		muiPaginationProps: {
			rowsPerPageOptions: [25, 50, 75, 100],
			showFirstButton: true,
			showLastButton: true,
		},
		muiTableContainerProps: {
			sx: {
				maxHeight: "calc(100vh - 376px)",
			},
		},
		renderToolbarInternalActions: ({ table }) => (
			<>
				<MRT_ToggleGlobalFilterButton id="vessel_view_global_search" table={table} />
				<MRT_ShowHideColumnsButton id="vessel_view_show_hide_Columns" table={table} />
				<MRT_ToggleFiltersButton id="vessel_view_global_filter" table={table} />
				<Button
						className="ml-2"
						onClick={() => {
							applyFilters();
						}}
					>
						Apply Filters
					</Button>
					<Button
						className="ml-2"
						variant="secondary"
						onClick={clearAllFilters}
					>
						Clear
					</Button>
			</>
		),
		muiTableHeadCellProps: ({ column }) => ({
			id: `${column.columnDef.accessorKey}`,
			sx: {
				color: "rgb(255 255 255)",

				"& .MuiButtonBase-root": {
					color: "white !important",
					"& .MuiTableSortLabel-icon": {
						color: "white !important",
					},
				},
				"& .MuiInputAdornment-root": {
					"& .Mui-disabled": {
						color: "darkgrey !important",
					},
				},
			},
		}),
		muiFilterTextFieldProps:  ({ column }) => ({
			sx: {
				"& .MuiInput-root": {
					color: "white !important",
				},
				"& .MuiButtonBase-root": {
					color: "white !important",
				},
				"& .MuiInputBase-root::after": {
					transform: "none",
					borderBottom: "2px solid white",
				},
			},
			onChange: (e) => {

				const value = e.target.value;

				updateFilterValue(column.id, value);
			},
			onKeyDown: (e) => {
				if (e.key === "Enter") {
					applyFilters();
				}
			},
		}),
		muiColumnActionsButtonProps: {
			sx: {
				color: "white",
			},
		},
		muiSelectAllCheckboxProps: {
			title: selectAll ? "Toggle deselect all" : "Toggle select all",
			onClick: (e) => handleSelectAll(e),
			sx: {
				color: "white",
			},
		},
		muiTableHeadRowProps: {
			sx: {
				backgroundColor: "#042a4c !important",

				"& .MuiTableCell-head::before": {
					backgroundColor: "#042a4c !important",
				},
			},
		},
		muiTablePaperProps: {
			sx: {
				boxShadow: "0 0 8px 2px rgba(0, 0, 0, 0.1)",
			},
		},
		muiSearchTextFieldProps: {
			placeholder: "Type and Enter",
			value: searchInput,

			onChange: (e) => {

				const value = e.target.value;

				setSearchInput(value);

				// auto reset when cleared
				if (!value.trim()) {

					setMyFleetPagination((prev) => ({
						...prev,
						pageIndex: 0,
					}));

					setMyFleetGlobalFilter("");
				}
			},

			onKeyDown: (e) => {
				if (e.key === "Enter") {
					applyFilters();
				}
			},
			InputProps: {
				endAdornment: searchInput && (
					<IconButton
						size="small"
						onClick={() => {
							setSearchInput("");
							setMyFleetGlobalFilter("");
							setMyFleetPagination((prev) => ({ ...prev, pageIndex: 0 }));
						}}
						sx={{ visibility: searchInput ? "visible" : "hidden" }}
					>
						<ClearIcon fontSize="small" />
					</IconButton>
				),
			},
		}
	});

	return (
		<>
			<div className="mt-4">
				<MaterialReactTable table={myFleetTable} />
			</div>
			<div className="confirmation-modal-container">
				<Modal
					isOpen={confirmDelete}
					modalHeader
					padding={"70px"}
					headerHeight={"50px"}
					showFooter
					onCloseClickListener={() => {
						setConfirmDelete(false);
					}}
					footer={
						<div className="d-flex justify-content-end fixed-action-btns p-4">
							<Button
								className="modal-delete-btn"
								onClick={handleDeletingRecord}
								primaryColor={COLORS.RED.DARK}
								primaryBackgroundColor={COLORS.RED.LIGHT}
							>
								Delete
							</Button>
							<Button
								className="modal-cancel-btn"
								onClick={() => {
									setConfirmDelete(false);
								}}
							>
								Cancel
							</Button>
						</div>
					}
				>
					<div>Are you sure you want to delete record(s)?</div>
				</Modal>

				{isImportVesselModalOpen && (
					<AbsModal isOpen={isImportVesselModalOpen} title="Import vessel(s) " onClose={setIsImportVesselModalOpen} close={toggleImportVessel}>
						<ImportVessel cancelImportVessel={toggleImportVessel} />
					</AbsModal>
				)}
			</div>
			
		</>
	);
};

export default MyFleetTable;
